// app/login/page.js
'use client'

import { useState, useEffect } from 'react'
import { supabase } from '../_utils/supabase'
import { useRouter } from 'next/navigation'
import { motion } from 'framer-motion'
import { BackgroundBeams } from '../_components/_ui/background-beams'
import Link from 'next/link'
import Logo from '../_components/Logo'
import FieldToastIndicator from '../_components/_ui/field-toast-indicator'
import { FaCheck, FaTimes, FaGoogle } from 'react-icons/fa'

// Add these validation functions at the top of your component
const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) return "Password must be at least 8 characters long";
    if (!hasUpperCase) return "Password must contain at least one uppercase letter";
    if (!hasLowerCase) return "Password must contain at least one lowercase letter";
    if (!hasNumbers) return "Password must contain at least one number";
    if (!hasSpecialChar) return "Password must contain at least one special character";
    return null;
};

const validateEmail = (email) => {
    // Basic email regex pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        return "Please enter a valid email address";
    }
    return null;
};

// Update the input field classes to ensure visibility
const inputClasses = `
    w-full px-4 py-2 rounded-md
    border border-gray-300
    focus:border-blue-500 focus:ring-2 focus:ring-blue-200
    bg-white/10 backdrop-blur-sm
    text-gray-900 dark:text-gray-100
    placeholder-gray-400
`

export default function SignUpPage() {
    // State declarations first
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [verifyPassword, setVerifyPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [isPasswordFocused, setIsPasswordFocused] = useState(false)
    const [isCheckingEmail, setIsCheckingEmail] = useState(false)
    const [isEmailAvailable, setIsEmailAvailable] = useState(null)
    
    const router = useRouter()

    // Requirements definition
    const requirements = [
        { test: p => p.length >= 8, text: "At least 8 characters" },
        { test: p => /[A-Z]/.test(p), text: "One uppercase letter" },
        { test: p => /[a-z]/.test(p), text: "One lowercase letter" },
        { test: p => /[0-9]/.test(p), text: "One number" },
        { test: p => /[!@#$%^&*(),.?":{}|<>]/.test(p), text: "One special character" }
    ]

    const isEmailValid = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isEmailAvailableAndValid = () => {
        return isEmailValid() && isEmailAvailable;
    };

    const isPasswordValid = () => {
        return requirements.every(req => req.test(password));
    };

    const isConfirmPasswordValid = () => {
        return password === verifyPassword && password.length > 0;
    };

    // Form validation after all validation functions
    const isFormValid = 
        isEmailAvailableAndValid() &&
        isPasswordValid() &&
        isConfirmPasswordValid() &&
        !loading;

    // Effects after validation functions
    useEffect(() => {
        const checkEmail = async () => {
            if (!isEmailValid() || email.length < 1) {
                setIsEmailAvailable(null)
                return
            }

            setIsCheckingEmail(true)
            try {
                const { data, error } = await supabase.auth.signInWithOtp({
                    email,
                    options: {
                        shouldCreateUser: false,
                    }
                })
                setIsEmailAvailable(true)
            } catch (error) {
                setIsEmailAvailable(false)
            } finally {
                setIsCheckingEmail(false)
            }
        }

        const timeoutId = setTimeout(checkEmail, 500)
        return () => clearTimeout(timeoutId)
    }, [email])

    useEffect(() => {
        // Check if the user is already logged in
        const checkUser = async () => {
            const {
                data: { session },
            } = await supabase.auth.getSession()
            if (session?.user) {
                // Redirect to the user's dashboard
                const username = session.user.email.split('@')[0] // Use email or another identifier as username
                router.push(`/${username}/dashboard/`)
            }
        }

        checkUser()
        
    }, [router])
    

    const handleSignup = async (e) => {
        e.preventDefault();
        
        if (!isFormValid) {
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            // Attempt signup
            const { data: authData, error: signupError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/callback`,
                    emailConfirm: false
                }
            });

            if (signupError) {
                // Handle specific error cases
                console.error('Signup error details:', signupError);
                if (signupError.message?.toLowerCase().includes('user already registered')) {
                    setError('This email is already registered. Please try signing in instead.');
                } else if (signupError.message?.toLowerCase().includes('email already in use')) {
                    setError('This email is already in use. Please try signing in instead.');
                } else {
                    setError(getErrorMessage(signupError));
                }
                setLoading(false);
                return;
            }

            if (!authData?.user?.id) {
                setError('Error during signup - no user created');
                setLoading(false);
                return;
            }

            setSuccessMessage('Signup successful! Please check your email to verify your account.');
            
        } catch (error) {
            console.error('Unexpected signup error - full details:', {
                message: error.message,
                code: error.code,
                status: error.status,
                name: error.name,
                stack: error.stack,
                fullError: error
            });
            setError('An unexpected error occurred during signup. Please try again later or contact support if the issue persists.');
            
            // Try simplified signup as a fallback
            trySimplifiedSignup(email, password);
        } finally {
            setLoading(false);
        }
    };
    
    // Simplified signup function for debugging
    const trySimplifiedSignup = async (email, password) => {
        console.log('Attempting simplified signup without metadata...');
        try {
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                // No metadata initially, but add email redirect and disable confirmation
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/callback`,
                    emailConfirm: false
                }
            });
            
            if (error) {
                console.error('Simplified signup also failed:', error);
            } else {
                console.log('Simplified signup succeeded!', data);
                setSuccessMessage('Account created! Please check your email to verify your account.');
            }
        } catch (fallbackError) {
            console.error('Simplified signup fallback error:', fallbackError);
        }
    };

    const getErrorMessage = (error) => {
        if (error.message.includes('auth/invalid-email')) {
            return 'Invalid email address.'
        } else if (error.message.includes('auth/user-not-found')) {
            return 'User not found. Please sign up.'
        } else if (error.message.includes('auth/wrong-password')) {
            return 'Incorrect password.'
        } else if (error.message.includes('auth/email-already-in-use')) {
            return 'Email already in use.'
        } else if (error.message.includes('auth/weak-password')) {
            return 'Password is too weak. It should be at least 6 characters long.'
        } else if (error.message.includes('auth/operation-not-allowed')) {
            return 'Operation not allowed. Please contact support.'
        } else {
            return 'An unexpected error occurred. Please try again.'
        }
    }

    const allRequirementsMet = password && requirements.every(req => req.test(password))

    const handleGoogleSignUp = async () => {
        try {
            setLoading(true)
            setError('')
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/auth/callback`
                }
            })
            if (error) throw error
        } catch (error) {
            setError('Error connecting to Google')
            console.error('Google signup error:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        // Check if user is already logged in
        const checkAuth = async () => {
            const { data: { session } } = await supabase.auth.getSession()
            if (session?.user) {
                // If user has username, redirect to dashboard
                if (session.user.user_metadata?.username) {
                    router.push(`/${session.user.user_metadata.username}/dashboard`)
                } else {
                    // If no username, redirect to username setup
                    router.push('/auth/username-setup')
                }
            }
        }

        checkAuth()
    }, [router])

    return (
        <div className="min-h-screen w-full relative flex items-center justify-center md:justify-end px-4">
            <BackgroundBeams className="opacity-40" />
            
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md p-8 mx-auto md:mx-12 my-10 
                         bg-white/90 dark:bg-white/10 backdrop-blur-lg rounded-2xl
                         border border-white/20 shadow-xl"
            >
                <div className="mb-8 text-center">
                    <Link href="/" className="inline-block mb-6">
                        <Logo size="large" />
                    </Link>
                    <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-4">
                        Create Account
                    </h1>
                    <div className="text-sm text-gray-600 dark:text-gray-300 mb-8 text-center">
                        <p className="mb-2">⚠️ Heads up! <br/> Right now sign-ups are restricted to certain emails addresses while we flesh out a functional application and fine tune areas where needed.</p>
                        <p className="mt-2">If you think you'd be a good fit for early access and want to help us test and shape the platform, feel free to reach out with your email before signing up:</p>
                        <p className="mt-1">📧 <a href="mailto:nate@autofoc.us" className="text-blue-500 hover:text-blue-400">nate@autofoc.us</a></p>
                        <p>📸 <a href="https://instagram.com/gld4rnr" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400">Instagram</a></p>
                    </div>
                </div>

                {successMessage && (
                    <motion.p 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-green-500 dark:text-green-400 text-sm text-center"
                    >
                        {successMessage}
                    </motion.p>
                )}

                <div className="mb-6">
                    <button
                        onClick={handleGoogleSignUp}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 
                                 border border-gray-300 dark:border-gray-600 rounded-lg
                                 text-gray-700 dark:text-gray-200 font-medium
                                 hover:bg-gray-50 dark:hover:bg-gray-700
                                 transition-colors duration-200"
                    >
                        <FaGoogle className="text-xl" />
                        Continue with Google
                    </button>
                </div>

                <div className="relative mb-6">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">
                            Or sign up with email
                        </span>
                    </div>
                </div>

                <form onSubmit={handleSignup} className="space-y-6" onClick={(e) => e.stopPropagation()}>
                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                            Email
                        </label>
                        {email && (
                            <FieldToastIndicator
                                visible={true}
                                message={isEmailValid() ? "Valid email format" : "Invalid email format"}
                                backgroundColor={isEmailValid() ? "#059669" : "#374151"}
                            />
                        )}
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={inputClasses}
                            required
                        />
                    </div>

                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                            Password
                        </label>
                        {password && (
                            <FieldToastIndicator
                                visible={true}
                                message={
                                    <div className="space-y-1">
                                        {requirements.map((req, index) => (
                                            <div key={index} className="flex items-center gap-2">
                                                {req.test(password) ? (
                                                    <FaCheck className="w-3 h-3 text-green-500" />
                                                ) : (
                                                    <FaTimes className="w-3 h-3 text-gray-400" />
                                                )}
                                                <span>{req.text}</span>
                                            </div>
                                        ))}
                                    </div>
                                }
                                backgroundColor={
                                    requirements.every(req => req.test(password))
                                        ? "#059669"  // Keep green for success
                                        : "#374151"  // Neutral gray for requirements
                                }
                            />
                        )}
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onFocus={() => setIsPasswordFocused(true)}
                            onBlur={() => setIsPasswordFocused(false)}
                            className={inputClasses}
                            required
                        />
                    </div>

                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                            Confirm Password
                        </label>
                        {verifyPassword && (
                            <FieldToastIndicator
                                visible={true}
                                message={password === verifyPassword ? "Passwords match" : "Passwords do not match"}
                                backgroundColor={password === verifyPassword ? "#059669" : "#374151"}
                            />
                        )}
                        <input
                            type="password"
                            value={verifyPassword}
                            onChange={(e) => setVerifyPassword(e.target.value)}
                            className={inputClasses}
                            required
                        />
                    </div>

                    {error && (
                        <motion.p 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-red-500 dark:text-red-400 text-sm text-center"
                        >
                            {error}
                        </motion.p>
                    )}

                    <motion.button
                        whileHover={{ scale: isFormValid ? 1.01 : 1 }}
                        whileTap={{ scale: isFormValid ? 0.99 : 1 }}
                        type="submit"
                        disabled={!isFormValid}
                        className={`w-full py-3 px-4 rounded-lg 
                            ${isFormValid 
                                ? 'bg-gradient-to-r from-blue-500 to-teal-400 cursor-pointer' 
                                : 'bg-gray-400 cursor-not-allowed'}
                            text-white font-medium shadow-lg
                            transition-all duration-200`}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Creating Account...
                            </span>
                        ) : (
                            'Create Account'
                        )}
                    </motion.button>

                    <div className="text-center space-y-2">
                        <p className="text-gray-600 dark:text-gray-400">
                            Already have an account?{' '}
                            <Link href="/login" className="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </form>
            </motion.div>
        </div>
    )
}
